/* eslint-disable no-unused-vars */
import { Box, makeStyles, Typography } from "@material-ui/core";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import ScoringToggleButton from "src/components/Button/ScoringToggleButton";
import { usePoolMonitoringContext } from "src/context/monitoring/poolMonitoringContext";
import { useProviderContext } from "src/ether-utils/context/providerContext";
import {
  encodePackageData,
  getContractAddressProviderFactory,
  getContractEditPool,
  getContractLendingPoolConfigurator,
} from "src/hooks/useContractEditPool";
import useLowerAddressParam from "src/hooks/useLowerAddressParam";
import useNotify from "src/hooks/useNotify";

const useStyle = makeStyles((theme) => ({
  text: {
    color: theme.palette.text.hint,
    marginBottom: 10,
  },
  hint: {
    color: theme.palette.text.hint,
  },
}));

export default function TabReserveState({ asset }) {
  const cls = useStyle();
  const { wallet, currentProvider } = useProviderContext();
  const { errorNotify, successNotify } = useNotify();
  const { data } = usePoolMonitoringContext();
  const { pool } = useLowerAddressParam();
  const { fetchPoolData } = usePoolMonitoringContext();
  const [enableBorrow, setEnableBorrow] = useState(asset.risk.enableBorrow);
  const [activeReserve, setActiveReserve] = useState(asset.risk.isActive);
  const [freezeReserve, setFreezeReserve] = useState(asset.risk.isFreezed);
  const [loading, setLoading] = useState(false);
  const [loadingBorrow, setLoadingBorrow] = useState(false);
  const [loadingActiveReserve, setLoadingActiveReserve] = useState(false);
  const [loadingFreezeReserve, setLoadingFreezeReserve] = useState(false);
  const history = useHistory();

  const handleToggle = async (type) => {
    setLoading(true);
    if (type == "borrow") setLoadingBorrow(true);
    if (type == "freezeReserve") setLoadingFreezeReserve(true);
    try {
      const contractAddressProviderFactory = getContractAddressProviderFactory(currentProvider);
      const addressProviderFactory = await contractAddressProviderFactory.getLendingPoolConfigurator(
        data[pool].overview.providerId
      );
      let tx;
      const contractLendingPoolConfigurator = getContractLendingPoolConfigurator(wallet.signer, addressProviderFactory);
      if (type == "borrow") {
        if (enableBorrow !== true)
          tx = await contractLendingPoolConfigurator.enableBorrowingOnReserve(asset.address, true);
        else tx = await contractLendingPoolConfigurator.disableBorrowingOnReserve(asset.address);
      }
      if (type == "freezeReserve") {
        if (freezeReserve !== true) tx = await contractLendingPoolConfigurator.freezeReserve(asset.address);
        else tx = await contractLendingPoolConfigurator.unfreezeReserve(asset.address);
      }
      await tx.wait();
      fetchPoolData();
      successNotify("Transaction Successfully");
    } catch (error) {
      console.error(error);
      errorNotify(error?.message);
    } finally {
      setLoading(false);
      setLoadingBorrow(false);
      setLoadingFreezeReserve(false);
    }
  };

  const handleToggleActiveReserve = async () => {
    setLoading(true);
    setLoadingActiveReserve(true);
    const contractEditPool = getContractEditPool(wallet.signer);
    try {
      let dataEncode;
      if (activeReserve !== true)
        dataEncode = await encodePackageData(currentProvider, "activateReserve", [asset.address]);
      else dataEncode = await encodePackageData(currentProvider, "deactivateReserve", [asset.address]);
      const tx = await contractEditPool.submitTransaction(data[pool].overview.providerId, dataEncode);
      await tx.wait();
      fetchPoolData();
      successNotify("Transaction Successfully");
      history.push(`/pool-monitoring/${pool}?action=pending`);
    } catch (error) {
      console.error(error);
      errorNotify(error?.message);
    } finally {
      setLoading(false);
      setLoadingActiveReserve(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography className={cls.hint}>Enable Borrow</Typography>
        <ScoringToggleButton
          loading={loadingBorrow}
          disabled={loading}
          isOn={enableBorrow}
          setIsOn={setEnableBorrow}
          handleClick={() => handleToggle("borrow")}
        />
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography className={cls.hint}>Freeze Reserve</Typography>
        <ScoringToggleButton
          loading={loadingFreezeReserve}
          disabled={loading}
          isOn={freezeReserve}
          setIsOn={setFreezeReserve}
          handleClick={() => handleToggle("freezeReserve")}
        />
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography className={cls.hint}>Active Reserve</Typography>
        <ScoringToggleButton
          loading={loadingActiveReserve}
          disabled={loading}
          isOn={activeReserve}
          setIsOn={setActiveReserve}
          handleClick={handleToggleActiveReserve}
        />
      </Box>
    </Box>
  );
}
